/**
 * ImportNetworkDialog — create a new, fully deployed network from an
 * exported StepFlow snapshot archive (``.zip``).
 *
 * Companion to the per-snapshot "Export manifest" action in
 * ``SnapshotsTab``. Together they give creators a portable format for
 * sharing a working network across tenants, AWS accounts, or
 * StepFlow installations.
 *
 * Intent boundaries (v3 — archive format):
 *
 *   * Exports are ``.zip`` archives containing ``manifest.json`` plus
 *     any future sibling artifacts (``bible.json``,
 *     ``chat_history.json``, per-agent files). We upload the raw
 *     archive base64-encoded so the server can unzip and process
 *     every file in one request, and so the wire format is stable
 *     when new sibling files get added.
 *   * The backend mints a fresh pet-name ``networkId`` on import —
 *     the creator does NOT name the network here. The manifest's
 *     ``network.name`` becomes the human-readable display name so
 *     the left panel and header read like the source network.
 *   * Import is *headless deploy*: the server compiles + deploys the
 *     Step Functions state machine, hydrates ``runtimeConfig`` from
 *     the importing tenant + region + account, replays chat history,
 *     and returns an ``ACTIVE`` network ready to run.
 *   * We sniff the archive client-side (unzip just ``manifest.json``)
 *     so the preview panel can show agent / state / chat counts
 *     before the user clicks Import. A corrupt archive produces an
 *     immediate error instead of a round trip.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { importNetworkFromManifest } from '@/services/networkRestoreApi';

// Generous browser-side ceiling — the API Gateway body limit is 10 MB;
// a realistic archive is <1 MB. The pre-parse gate here just avoids
// the user waiting on a .bin they picked by mistake.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Client-side unzip of a single entry using the browser-native
 * ``DecompressionStream`` (available in every browser StepFlow
 * supports). We only read ``manifest.json`` for the preview — the
 * server is the real consumer and re-validates the whole archive.
 *
 * We parse the ZIP central directory manually (stdlib-free) to pick
 * out the ``manifest.json`` entry. This keeps the UI bundle small;
 * pulling in JSZip would add ~100 KB for a ~100-line task.
 */
async function extractManifestJson(archiveFile) {
  const buf = new Uint8Array(await archiveFile.arrayBuffer());
  // Locate End-of-central-directory (EOCD) record — signature 0x06054b50.
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 0xffff - 22); i--) {
    if (
      buf[i] === 0x50 && buf[i + 1] === 0x4b &&
      buf[i + 2] === 0x05 && buf[i + 3] === 0x06
    ) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('Not a valid ZIP archive.');
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const cdSize = view.getUint32(eocd + 12, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  let p = cdOffset;
  const end = cdOffset + cdSize;
  while (p < end) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const compSize = view.getUint32(p + 20, true);
    const uncompSize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localHeader = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(buf.subarray(p + 46, p + 46 + nameLen));
    if (name === 'manifest.json') {
      // Local file header: 30 bytes fixed + nameLen + extraLen,
      // THEN the data. Local extraLen may differ from central extraLen.
      const localNameLen = view.getUint16(localHeader + 26, true);
      const localExtraLen = view.getUint16(localHeader + 28, true);
      const dataStart = localHeader + 30 + localNameLen + localExtraLen;
      const compData = buf.subarray(dataStart, dataStart + compSize);
      let raw;
      if (method === 0) {
        raw = compData;
      } else if (method === 8) {
        const stream = new Blob([compData]).stream().pipeThrough(
          new DecompressionStream('deflate-raw'),
        );
        const chunks = [];
        const reader = stream.getReader();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          chunks.push(value);
        }
        const total = chunks.reduce((n, c) => n + c.length, 0);
        raw = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) {
          raw.set(c, off);
          off += c.length;
        }
        if (uncompSize && raw.length !== uncompSize) {
          // Mismatch is suspicious but not fatal — servers still
          // validate. Don't fail the preview over a length header.
          // eslint-disable-next-line no-console
          console.warn('[import] manifest.json size mismatch');
        }
      } else {
        throw new Error(`Unsupported ZIP compression method: ${method}`);
      }
      return JSON.parse(new TextDecoder().decode(raw));
    }
    p += 46 + nameLen + extraLen + commentLen;
  }
  throw new Error('Archive does not contain manifest.json.');
}

/**
 * Minimal client-side sniff. Only asserts the shape the server must
 * reject on first — ``manifestVersion`` (required) and either
 * ``interface.asl`` or ``agents`` so we don't waste an import call on
 * an empty document. Full semantic validation is server-side.
 */
function assertManifestShape(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    throw new Error('Manifest must be a JSON object.');
  }
  if (obj.manifestVersion == null) {
    throw new Error(
      'Manifest is missing "manifestVersion". Is this an exported StepFlow archive?',
    );
  }
  const hasAgents = Array.isArray(obj.agents) && obj.agents.length > 0;
  const hasAsl = obj.interface && typeof obj.interface === 'object'
    && obj.interface.asl && typeof obj.interface.asl === 'object';
  if (!hasAgents && !hasAsl) {
    throw new Error('Manifest is empty — no agents or interface.asl blocks were found.');
  }
}

/**
 * The first user-authored prompt in the chat history is the closest
 * thing the manifest carries to "what was this network built for?".
 * Surface it in the preview panel so the creator can sanity-check
 * the intent before importing.
 */
function firstUserIntent(chatHistory) {
  if (!Array.isArray(chatHistory)) return null;
  for (const msg of chatHistory) {
    if (msg && msg.role === 'user' && typeof msg.content === 'string') {
      const trimmed = msg.content.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
}

export default function ImportNetworkDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setManifest(null);
    setParseError(null);
    setSubmitError(null);
    setSubmitting(false);
  }, [open]);

  const manifestSummary = React.useMemo(() => {
    if (!manifest) return null;
    const agentCount = Array.isArray(manifest.agents) ? manifest.agents.length : 0;
    const stateCount = manifest.interface?.asl?.States
      && typeof manifest.interface.asl.States === 'object'
      ? Object.keys(manifest.interface.asl.States).length
      : 0;
    const chatHistory = Array.isArray(manifest.chatHistory) ? manifest.chatHistory : [];
    return {
      agentCount,
      stateCount,
      manifestVersion: manifest.manifestVersion,
      sourceName: typeof manifest.name === 'string' ? manifest.name : null,
      chatTurnCount: chatHistory.length,
      originalIntent: firstUserIntent(chatHistory),
    };
  }, [manifest]);

  const onFileChange = async (e) => {
    const picked = e.target.files?.[0] || null;
    setFile(picked);
    setManifest(null);
    setParseError(null);
    setSubmitError(null);
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) {
      setParseError('File is too large (max 5 MB).');
      return;
    }
    try {
      const parsed = await extractManifestJson(picked);
      assertManifestShape(parsed);
      setManifest(parsed);
    } catch (err) {
      setParseError(err?.message || 'Could not read archive.');
    }
  };

  const canSubmit = !!file && !!manifest && !submitting && !parseError;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Upload the whole archive (not just the unzipped manifest) so
      // future sibling artifacts inside the ``.zip`` round-trip
      // without a wire-format bump.
      const res = await importNetworkFromManifest({ archive: file });
      onImported?.(res);
    } catch (e) {
      setSubmitError(e?.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="import-network-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={onClose}
          onKeyDown={onKeyDown}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="text-sm font-semibold text-gray-900">Import network</h3>
              <p className="mt-1 text-xs text-gray-600">
                Upload an exported StepFlow archive (
                <span className="font-mono text-[11px] rounded bg-gray-100 px-1 py-0.5">
                  .stepflow.zip
                </span>
                ). StepFlow will mint a fresh network id, deploy the Step
                Functions state machine, and open an{' '}
                <strong className="font-medium text-gray-700">Active</strong>{' '}
                network ready to run.
              </p>
            </div>

            <div className="space-y-3 px-5 py-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">
                  Snapshot archive
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/zip,.zip"
                  onChange={onFileChange}
                  className="mt-1 block w-full text-xs text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-blue-700 hover:file:bg-blue-100"
                />
              </label>

              {parseError && (
                <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {parseError}
                </p>
              )}

              {manifestSummary && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-700 space-y-1">
                  <p>
                    <span className="font-medium text-gray-800">Manifest v{manifestSummary.manifestVersion}</span>
                    {manifestSummary.sourceName ? (
                      <> · Display name <span className="font-mono">{manifestSummary.sourceName}</span></>
                    ) : null}
                  </p>
                  <p>
                    {manifestSummary.agentCount}{' '}
                    {manifestSummary.agentCount === 1 ? 'agent' : 'agents'}
                    {' · '}
                    {manifestSummary.stateCount}{' '}
                    {manifestSummary.stateCount === 1 ? 'graph state' : 'graph states'}
                    {' · '}
                    {manifestSummary.chatTurnCount}{' '}
                    {manifestSummary.chatTurnCount === 1 ? 'chat turn' : 'chat turns'}
                  </p>
                  {manifestSummary.originalIntent && (
                    <div className="mt-1 rounded border border-gray-200 bg-white px-2 py-1.5">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        Original intent
                      </p>
                      <p className="mt-0.5 line-clamp-3 text-[11px] text-gray-700">
                        {manifestSummary.originalIntent}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <p className="rounded-lg bg-blue-50 px-3 py-2 text-[11px] leading-relaxed text-blue-800">
                A new pet-name network id is generated and the Step
                Functions graph is deployed into{' '}
                <em>this</em> tenant + AWS account. The original
                source identifiers are not carried over.
              </p>

              {submitError && (
                <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {submitError}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Importing…' : 'Import & deploy'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
